import React from "react";
import { MapContainer, TileLayer, Circle, CircleMarker, Marker, Popup, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const Popoyo = { lat: 11.2526, lon: -85.8708 };

const popoyoIcon = L.divIcon({
  html: `<div style="background:#1B3B6F;width:18px;height:18px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 2px #1B3B6F, 0 4px 10px -3px rgba(27,59,111,0.7);"></div>`,
  className: "",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

function magColor(m) {
  if (m == null) return "#5C757D";
  if (m >= 6.5) return "#7a1f1f";
  if (m >= 6) return "#b21f1f";
  if (m >= 5.5) return "#D62828";
  if (m >= 5) return "#E76F51";
  return "#F4A261";
}

function magRadius(m) {
  if (m == null) return 6;
  // Scale: M5 → 8px, M6 → 14px, M7 → 22px
  return Math.max(6, Math.round(4 + Math.pow(Math.max(0, m - 3), 1.6) * 1.2));
}

export default function EarthquakeMap({ quakes = [], radiusMiles = 25 }) {
  const radiusKm = radiusMiles * 1.609344;
  const radiusMeters = radiusKm * 1000;
  // Choose zoom so the 25 mile circle fits comfortably
  const zoom = radiusMiles <= 25 ? 9 : radiusMiles <= 50 ? 8 : 7;

  return (
    <div
      data-testid="earthquake-map"
      className="relative rounded-2xl overflow-hidden border border-[color:var(--hairline)]"
      style={{ height: 240 }}
    >
      <MapContainer
        center={[Popoyo.lat, Popoyo.lon]}
        zoom={zoom}
        minZoom={6}
        maxZoom={11}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ height: "100%", width: "100%", background: "#EAF1EE" }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
        />

        {/* Radius circle */}
        <Circle
          center={[Popoyo.lat, Popoyo.lon]}
          radius={radiusMeters}
          pathOptions={{
            color: "#1B3B6F",
            weight: 1.5,
            opacity: 0.7,
            fillColor: "#1B3B6F",
            fillOpacity: 0.06,
            dashArray: "4 4",
          }}
        />

        {/* Popoyo marker */}
        <Marker position={[Popoyo.lat, Popoyo.lon]} icon={popoyoIcon}>
          <Tooltip direction="top" offset={[0, -10]} permanent={false}>
            Popoyo
          </Tooltip>
        </Marker>

        {/* Earthquake epicenters */}
        {quakes
          .filter((q) => q.lat != null && q.lon != null)
          .map((q) => (
            <CircleMarker
              key={q.id}
              center={[q.lat, q.lon]}
              radius={magRadius(q.magnitude)}
              pathOptions={{
                color: "#ffffff",
                weight: 1.5,
                fillColor: magColor(q.magnitude),
                fillOpacity: 0.85,
              }}
            >
              <Popup>
                <strong>M {q.magnitude?.toFixed(1)}</strong>
                <br />
                {q.place}
                <br />
                <span style={{ fontSize: 11, color: "#5C757D" }}>
                  {q.distance_miles != null && <>{q.distance_miles} mi from Popoyo · </>}
                  depth {q.depth_km?.toFixed(0)} km
                </span>
              </Popup>
            </CircleMarker>
          ))}
      </MapContainer>

      {/* Overlay legend */}
      <div className="absolute bottom-2 left-2 right-2 sm:right-auto rounded-xl bg-white/90 backdrop-blur px-2.5 py-1.5 text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] pointer-events-none flex items-center gap-3">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[color:var(--navy)]" />
          Popoyo
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full border border-[color:var(--navy)]/40" style={{ background: "rgba(27,59,111,0.06)" }} />
          {radiusMiles} mi
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#E76F51" }} />
          M5
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: "#D62828" }} />
          M5.5+
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 rounded-full" style={{ background: "#b21f1f" }} />
          M6+
        </span>
      </div>
    </div>
  );
}
